import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Card,
  Button,
  Input,
  Select,
  FormField,
  Alert,
  FileUpload,
  DatePicker,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { getSubunitMultiplier } from '@bbpos/utils';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { expenseService } from '@/services/expense.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { CATEGORY_OPTIONS } from '@/types/expense';
import { toLocalIsoDate } from './expenses/date-utils';
import { resolveReceiptUrl, ReceiptUploadError } from './expense-receipt-upload';
import type { ExpenseCategory } from '@/types/expense';

const LOCATION_OPTIONS_PLACEHOLDER = [{ value: '', label: 'Select location\u2026' }];

function ExpenseCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const currencySymbol = getCurrencySymbol(currency);
  const profile = useAuthStore((s) => s.profile);
  const formTouched = useRef(false);

  const locationOptions = [
    { value: '', label: 'Select location\u2026' },
    ...(profile?.locations ?? [])
      .filter((l: { is_active: boolean }) => l.is_active)
      .map((l: { id: string; name: string }) => ({ value: l.id, label: l.name })),
  ];

  const autoLocation =
    locationOptions.length === 2 ? locationOptions[1].value : '';

  const [form, setForm] = useState({
    amountRupees: '',
    category: '' as ExpenseCategory | '',
    description: '',
    expense_date: new Date(),
    location_id: autoLocation,
    reference: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [showBackdateWarning, setShowBackdateWarning] = useState(false);
  const [showReceiptWarning, setShowReceiptWarning] = useState(false);
  const [receiptWarningDismissed, setReceiptWarningDismissed] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateMinutesAgo, setDuplicateMinutesAgo] = useState(0);
  const [receiptUploadFailed, setReceiptUploadFailed] = useState(false);

  // Backdate detection
  useEffect(() => {
    if (form.expense_date) {
      const today = new Date();
      const diffMs = today.getTime() - form.expense_date.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      setShowBackdateWarning(diffDays > 7);
    }
  }, [form.expense_date]);

  // Receipt warning — only show after user has touched the form, and not dismissed
  useEffect(() => {
    if (formTouched.current && !receiptFile && !receiptWarningDismissed) {
      setShowReceiptWarning(true);
    } else {
      setShowReceiptWarning(false);
    }
  }, [receiptFile, receiptWarningDismissed]);

  function patch<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    formTouched.current = true;
    setForm((f) => ({ ...f, [key]: value }));
  }

  // ── Duplicate detection (FR-EXP-018) ──────────────────────────────────────
  // Check recent expenses for same amount + category within 30 min

  const amtPaise = Math.round(parseFloat(form.amountRupees || '0') * getSubunitMultiplier(currency));

  // Detection runs server-side: matched on submission recency (created_at)
  // within 30 min, scoped to the selected location + category + amount, so a
  // backdated entry is still caught and a different location is not flagged.
  const { data: duplicateResult } = useQuery({
    queryKey: ['expense-duplicate-check', amtPaise, form.category, form.location_id],
    queryFn: () =>
      expenseService.checkDuplicate({
        location_id: form.location_id,
        category: form.category as string,
        amount: amtPaise,
      }),
    enabled: amtPaise > 0 && !!form.category && !!form.location_id,
    staleTime: 30_000,
  });

  useEffect(() => {
    const duplicate = duplicateResult?.duplicate;
    if (!duplicate || amtPaise <= 0 || !form.category) {
      setShowDuplicateWarning(false);
      return;
    }
    setShowDuplicateWarning(true);
    setDuplicateMinutesAgo(
      Math.round((Date.now() - new Date(duplicate.created_at).getTime()) / 60_000),
    );
  }, [duplicateResult, amtPaise, form.category]);

  // ── Mutation ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (skipReceipt: boolean) => {
      const amount = Math.round(parseFloat(form.amountRupees) * getSubunitMultiplier(currency));

      // Store the hosted file URL (not the raw S3 key) so the detail page can
      // render it directly as an <img src>. See issue #201. If the upload fails
      // this throws ReceiptUploadError and the expense is NOT created \u2014 we must
      // not silently submit a receipt-less expense. See issue #202.
      const receipt_url = await resolveReceiptUrl(receiptFile, skipReceipt);

      return expenseService.create({
        amount,
        category: form.category as ExpenseCategory,
        description: form.description.trim(),
        expense_date: toLocalIsoDate(form.expense_date),
        location_id: form.location_id,
        reference: form.reference.trim() || undefined,
        receipt_url,
      });
    },
    onSuccess: () => {
      setReceiptUploadFailed(false);
      toast({ title: 'Expense submitted for approval', variant: 'success' });
      navigate('/expenses');
    },
    onError: (err: Error) => {
      if (err instanceof ReceiptUploadError) {
        setReceiptUploadFailed(true);
        toast({
          title: 'Receipt upload failed \u2014 the expense was not submitted. Retry, or submit without the receipt.',
          variant: 'error',
        });
        return;
      }
      toast({ title: err.message, variant: 'error' });
    },
  });

  const handleSubmit = useCallback(() => {
    const errs: Record<string, string> = {};
    const amtNum = parseFloat(form.amountRupees);
    if (!form.amountRupees || isNaN(amtNum) || amtNum <= 0) errs.amount = 'Enter a valid amount';
    if (!form.category) errs.category = 'Category is required';
    if (!form.description.trim() || form.description.trim().length < 3)
      errs.description = 'Description must be at least 3 characters';
    if (form.description.trim().length > 500) errs.description = 'Description must be 500 chars or less';
    if (!form.location_id) errs.location_id = 'Location is required';
    if (form.expense_date > new Date()) errs.expense_date = 'Expense date cannot be in the future';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setReceiptUploadFailed(false);
    createMutation.mutate(false);
  }, [form, createMutation]);

  // Ctrl+Enter to submit
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleSubmit]);

  // Live preview of amount in paise
  const previewAmount = parseFloat(form.amountRupees);
  const hasValidAmount = !isNaN(previewAmount) && previewAmount > 0;

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-warning-600 to-danger-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-white">New Expense</h1>
        <p className="text-warning-100 mt-1">Submit an expense for approval</p>
      </div>

      <div className="max-w-xl mx-auto animate-slide-up">
        <Card className="p-6 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-lg rounded-xl">
          <div className="space-y-4">
            {/* Amount — prominent */}
            <FormField label={`Amount (${currencySymbol})`} required error={formErrors.amount}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-warning-600 dark:text-warning-400 font-bold pointer-events-none">
                  {currencySymbol}
                </span>
                <Input
                  type="text"
                  value={form.amountRupees}
                  onChange={(v) => patch('amountRupees', v)}
                  placeholder="0.00"
                  className={`${currencySymbol.length > 1 ? 'pl-16' : 'pl-8'} text-lg font-semibold tabular-nums`}
                />
              </div>
              {hasValidAmount && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 tabular-nums">
                  {formatPaise(Math.round(previewAmount * getSubunitMultiplier(currency)), currency)}
                </p>
              )}
            </FormField>

            {/* Category */}
            <FormField label="Category" required error={formErrors.category}>
              <Select
                options={[{ value: '', label: 'Select category\u2026' }, ...CATEGORY_OPTIONS]}
                value={form.category}
                onChange={(v) => patch('category', (typeof v === 'string' ? v : '') as ExpenseCategory | '')}
                placeholder={'Select category\u2026'}
              />
            </FormField>

            {/* Duplicate detection warning (FR-EXP-018) */}
            {showDuplicateWarning && (
              <Alert
                variant="warning"
                title={`A similar expense (${formatPaise(amtPaise, currency)}, ${form.category ? CATEGORY_OPTIONS.find((c) => c.value === form.category)?.label ?? form.category : ''}) was submitted ${duplicateMinutesAgo} minute${duplicateMinutesAgo !== 1 ? 's' : ''} ago. Continue?`}
              />
            )}

            {/* Description */}
            <FormField label="Description" required error={formErrors.description}>
              <textarea
                value={form.description}
                onChange={(e) => patch('description', e.target.value)}
                placeholder={'Describe the expense (3\u2013500 characters)\u2026'}
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg bg-white/60 dark:bg-neutral-800/60 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-warning-500 focus:border-transparent resize-none shadow-sm transition-all duration-200"
              />
            </FormField>

            {/* Expense Date */}
            <FormField label="Expense Date" required error={formErrors.expense_date}>
              <DatePicker
                variant="single"
                value={form.expense_date}
                onChange={(d) => d && patch('expense_date', d as Date)}
                maxDate={new Date()}
                placeholder="Select date"
              />
            </FormField>

            {showBackdateWarning && (
              <Alert
                variant="warning"
                title="Expenses older than 7 days require manager approval."
              />
            )}

            {/* Location */}
            <FormField label="Location" required error={formErrors.location_id}>
              <Select
                options={locationOptions.length > 1 ? locationOptions : LOCATION_OPTIONS_PLACEHOLDER}
                value={form.location_id}
                onChange={(v) => patch('location_id', typeof v === 'string' ? v : '')}
                placeholder={'Select location\u2026'}
              />
            </FormField>

            {/* Reference */}
            <FormField label="Reference / Invoice #">
              <Input
                value={form.reference}
                onChange={(v) => patch('reference', v)}
                placeholder="Optional invoice or reference number"
              />
            </FormField>

            {/* Receipt */}
            <FormField label="Receipt Photo">
              <FileUpload
                accept="image/jpeg,image/png"
                maxSize={5 * 1024 * 1024}
                onUpload={(files: File[]) => {
                  setReceiptFile(files[0] ?? null);
                  if (files[0]) setReceiptWarningDismissed(false);
                }}
              />
            </FormField>

            {showReceiptWarning && (
              <Alert
                variant="warning"
                title="Attaching a receipt is strongly recommended for audit compliance."
                dismissible
                onDismiss={() => setReceiptWarningDismissed(true)}
              />
            )}

            {/* Receipt upload failed (#202) — the expense was NOT submitted.
                Force an explicit choice instead of silently dropping the receipt. */}
            {receiptUploadFailed && (
              <div className="space-y-2">
                <Alert
                  variant="danger"
                  title="Receipt upload failed — the expense has not been submitted. Check your connection and retry, or choose to submit without the receipt."
                />
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => createMutation.mutate(false)}
                    disabled={createMutation.isPending}
                  >
                    Retry upload
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => createMutation.mutate(true)}
                    disabled={createMutation.isPending}
                  >
                    Submit without receipt
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => navigate('/expenses')}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={createMutation.isPending}
              >
                Submit Expense
              </Button>
            </div>

            <p className="text-xs text-neutral-400 dark:text-neutral-500 text-right">
              Ctrl+Enter to submit
            </p>
          </div>
        </Card>
      </div>
    </>
  );
}

export function ExpenseCreatePage() {
  return (
    <PageContainer
      title="New Expense"
      breadcrumbs={[{ label: 'Expenses', href: '/expenses' }, { label: 'New Expense' }]}
    >
      <ErrorBoundary>
        <ExpenseCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
